import { formatDistanceToNow } from 'date-fns';
import {
  FileText,
  FileType2,
  File,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderInput,
  FolderMinus,
  MessageSquare,
  Play,
  Copy,
  ExternalLink,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Source, ExtractionStatus } from '@/types/database';

interface SourceCardProps {
  source: Source;
  isSelected?: boolean;
  onSelect?: (sourceId: string) => void;
  onClick?: (sourceId: string) => void;
  onEdit?: (sourceId: string) => void;
  onDelete?: (sourceId: string) => void;
  onMove?: (sourceId: string) => void;
  onChat?: (sourceId: string) => void;
  onEvaluate?: (sourceId: string) => void;
  onRemoveFromFolder?: (sourceId: string) => void;
}

const fileTypeConfig: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  'application/pdf': { icon: FileText, label: 'PDF', color: 'text-rose-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    icon: FileType2,
    label: 'DOCX',
    color: 'text-blue-500',
  },
  'text/plain': { icon: File, label: 'TXT', color: 'text-slate-500' },
  'application/x-research-info-systems': { icon: FileText, label: 'RIS', color: 'text-emerald-600' },
  'application/x-bibtex': { icon: FileText, label: 'BIB', color: 'text-amber-600' },
};

const extractionStatusConfig: Record<
  ExtractionStatus,
  { icon: typeof Clock; label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; className: string }
> = {
  pending: { icon: Clock, label: 'Pending', variant: 'secondary', className: 'text-slate-500' },
  processing: { icon: Loader2, label: 'Extracting…', variant: 'outline', className: 'text-amber-500' },
  completed: { icon: CheckCircle2, label: 'Extracted', variant: 'default', className: 'text-emerald-500' },
  failed: { icon: XCircle, label: 'Failed', variant: 'destructive', className: 'text-rose-500' },
};

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatAuthors(authors: string[] | null): string {
  if (!authors || authors.length === 0) return '';
  if (authors.length <= 2) return authors.join(' & ');
  return `${authors.join(', ').split(', ')[0]} et al.`;
}

export function SourceCard({
  source,
  isSelected = false,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  onMove,
  onChat,
  onEvaluate,
  onRemoveFromFolder,
}: SourceCardProps) {
  const fileConfig = fileTypeConfig[source.file_type] ?? {
    icon: File,
    label: (source.file_type.split('/').pop() ?? 'FILE').toUpperCase(),
    color: 'text-slate-400',
  };
  const FileIcon = fileConfig.icon;

  const statusConfig = extractionStatusConfig[source.extraction_status];
  const StatusIcon = statusConfig.icon;

  const displayTitle = source.title ?? source.file_name;
  const authorsText = formatAuthors(source.authors);
  const fileSizeText = formatFileSize(source.file_size);
  const timeAgo = formatDistanceToNow(new Date(source.created_at), { addSuffix: true });

  const handleClick = () => {
    if (onClick) {
      onClick(source.id);
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(source.id);
  };

  return (
    <Card
      className={cn(
        'group transition-all hover:shadow-md hover:border-indigo-200',
        onClick && 'cursor-pointer',
        isSelected && 'border-indigo-400 ring-1 ring-indigo-200 bg-indigo-50/30'
      )}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Selection checkbox area / File icon */}
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
              isSelected ? 'bg-indigo-100' : 'bg-slate-50 group-hover:bg-slate-100'
            )}
            onClick={onSelect ? handleSelect : undefined}
          >
            <FileIcon className={cn('h-5 w-5', fileConfig.color)} />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Title row */}
            <div className="flex items-start gap-2">
              <TooltipProvider delayDuration={400}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="truncate text-sm font-medium text-slate-900">
                      {displayTitle}
                    </h3>
                  </TooltipTrigger>
                  {displayTitle.length > 40 && (
                    <TooltipContent side="top" className="max-w-sm">
                      <p>{displayTitle}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>

              {/* File type badge */}
              <Badge variant="outline" className="shrink-0 text-xs">
                {fileConfig.label}
              </Badge>

              {/* Extraction status */}
              <Badge variant={statusConfig.variant} className="shrink-0 gap-1 text-xs">
                <StatusIcon
                  className={cn(
                    'h-3 w-3',
                    statusConfig.className,
                    source.extraction_status === 'processing' && 'animate-spin'
                  )}
                />
                {statusConfig.label}
              </Badge>

              {/* Content depth indicator for completed non-PDF sources */}
              {source.extraction_status === 'completed' && !source.file_path && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={cn(
                          'shrink-0 text-[10px]',
                          (source.metadata as Record<string, unknown>)?.extraction_source === 'doi_lookup' ||
                          (source.metadata as Record<string, unknown>)?.extraction_source === 'abstract_fallback'
                            ? 'border-amber-200 text-amber-600'
                            : 'border-slate-200 text-slate-500'
                        )}
                      >
                        {(source.metadata as Record<string, unknown>)?.extraction_source === 'doi_lookup' ||
                         (source.metadata as Record<string, unknown>)?.extraction_source === 'abstract_fallback'
                          ? 'Abstract'
                          : 'Metadata'}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {(source.metadata as Record<string, unknown>)?.extraction_source === 'doi_lookup'
                          ? 'Abstract retrieved via DOI lookup'
                          : (source.metadata as Record<string, unknown>)?.extraction_source === 'abstract_fallback'
                            ? 'Abstract from import file'
                            : 'Only metadata available (title, authors, DOI)'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {source.extraction_status === 'completed' && !!source.file_path && (() => {
                const extractionSource = (source.metadata as Record<string, unknown>)?.extraction_source as string | undefined;
                const isExternalPdf = extractionSource === 'pdf_with_abstract' || extractionSource === 'pdf_with_metadata';
                return (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className={cn(
                          'shrink-0 text-[10px]',
                          isExternalPdf ? 'border-emerald-200 text-emerald-600' : 'border-emerald-200 text-emerald-600'
                        )}>
                          {isExternalPdf ? 'PDF + Abstract' : 'Full text'}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          {isExternalPdf
                            ? 'PDF viewable, abstract used for chat & evaluation'
                            : 'Full text extracted from PDF'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })()}
            </div>

            {/* Bibliographic metadata */}
            {(authorsText || source.journal || source.publication_year) && (
              <p className="mt-1 truncate text-xs text-slate-500">
                {authorsText}
                {authorsText && source.publication_year ? ` (${source.publication_year})` : ''}
                {!authorsText && source.publication_year ? `${source.publication_year}` : ''}
                {source.journal ? ` — ${source.journal}` : ''}
              </p>
            )}

            {/* DOI link */}
            {source.doi && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-indigo-500">
                <ExternalLink className="h-3 w-3" />
                <span className="truncate">DOI: {source.doi}</span>
              </p>
            )}

            {/* Duplicate warning */}
            {source.is_duplicate && (
              <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3" />
                <span>Potential duplicate</span>
              </div>
            )}

            {/* Keywords */}
            {source.keywords && source.keywords.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {source.keywords.slice(0, 4).map((kw) => (
                  <Badge
                    key={kw}
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0 font-normal"
                  >
                    {kw}
                  </Badge>
                ))}
                {source.keywords.length > 4 && (
                  <span className="text-[10px] text-slate-400">
                    +{source.keywords.length - 4}
                  </span>
                )}
              </div>
            )}

            {/* Footer: file size + time */}
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
              {fileSizeText && <span>{fileSizeText}</span>}
              {fileSizeText && <span>&middot;</span>}
              <span>{timeAgo}</span>
            </div>
          </div>

          {/* Actions menu */}
          {(onEdit || onDelete || onMove || onChat || onEvaluate || onRemoveFromFolder) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {onChat && source.extraction_status === 'completed' && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onChat(source.id);
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Chat with source
                  </DropdownMenuItem>
                )}
                {onEvaluate && source.extraction_status === 'completed' && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEvaluate(source.id);
                    }}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Evaluate
                  </DropdownMenuItem>
                )}
                {(onChat || onEvaluate) && source.extraction_status === 'completed' && (onEdit || onMove || onDelete) && (
                  <DropdownMenuSeparator />
                )}
                {onEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(source.id);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit metadata
                  </DropdownMenuItem>
                )}
                {onMove && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onMove(source.id);
                    }}
                  >
                    <FolderInput className="mr-2 h-4 w-4" />
                    Add/Move to folder…
                  </DropdownMenuItem>
                )}
                {onRemoveFromFolder && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromFolder(source.id);
                    }}
                  >
                    <FolderMinus className="mr-2 h-4 w-4" />
                    Remove from folder
                  </DropdownMenuItem>
                )}
                {source.is_duplicate && (
                  <DropdownMenuItem disabled>
                    <Copy className="mr-2 h-4 w-4" />
                    View duplicate
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-rose-600 focus:text-rose-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(source.id);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
